import { useEffect, useLayoutEffect, useRef, useState } from "react"
import styled from "@emotion/styled"
import hash from "@emotion/hash"
import { animate, motion, useMotionValue } from "framer-motion"
import getMediaQuery from "css/breakpoints"
import { useFrameAnimation, STEPS_LENGTH, DIAL_SCALE, DIAL_OFFSET_Y } from "./FrameAnimation"
import { mvSubscribe, rafDelay, setProperty } from "./utils"
import { CfgNavigation } from "./navigation"
import { CfgHeader } from "./header"
import { CfgBackgrounds, CfgBackgroundWatch } from "./backgrounds"
import { CfgSteps } from "./steps"
import { CfgRoller } from "./dials"
import { CfgCanvasPanels } from "./canvas"
import { CfgCanvasTabs } from "./canvas"
import { CfgCatalog } from "./catalog"
import { CfgHash } from "./hash"
import { CfgActive } from "./active"
import { CfgLauncherCard } from "./launcher"
import { CfgInstall } from "./install.js"
import { SWAPPING_DELAY } from "./constants.js"
import { useConfigurator } from "components/configurator-v3/context"
import { pageGradientVar } from "./../model/colors"

const Contents = styled.section`
  position: relative;
  justify-content: end;
  align-items: start;
  box-sizing: border-box;
  width: 100%;
  min-width: 320px;
  height: 100%;
  min-height: 500px;
  
  overflow: hidden;

  &.dark-theme {
    background-color: black;
  }
  &.light-theme {
    background: linear-gradient(to top, #f0f0f0, #fff);
  }
  display: grid;
  grid-template-columns: 1fr;

  ${getMediaQuery("m")} {
    min-height: 700px;
    overflow: auto;
  }

  &.cfg-install {
    overflow: hidden;
    min-height: 0px;

    ${getMediaQuery("m")} {
      min-height: 0px;
      overflow: hidden;
    }
  }
  &.wv_launched {
    & > section {
      transition: opacity 200ms cubic-bezier(0.61, 1, 0.88, 1) 0ms, visibility 0ms 200ms;
      opacity: 0;
      visibility: hidden;
    }
    /*    & > div {
      transition: opacity 600ms cubic-bezier(0.61, 1, 0.88, 1) 100ms, visibility 0ms 0ms;
      opacity: 1;
      visibility: inherit;
    }*/
  }

  & > section {
    grid-row: 1/-1;
    grid-column: 1/-1;
    position: relative;
    height: 100%;
    z-index: 29;
    background: linear-gradient(to top, #f0f0f0, #fff);
  }

  & > div {
    grid-row: 1/-1;
    grid-column: 1/-1;
    position: relative;
    height: 100%;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
    //    opacity: 0;
    //    visibility: hidden;

    &.cfgv7_frozen {
      //      pointer-events: none;
    }
  }

  @keyframes wvreveal {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  --cfg-dial-offset: ${DIAL_OFFSET_Y.landscape}%;
  --cfg-dial-shrink: ${DIAL_SCALE.landscape}%;
  ${getMediaQuery("m", { max: true })} {
    --cfg-dial-offset: ${DIAL_OFFSET_Y.portrait}%;
    --cfg-dial-shrink: ${DIAL_SCALE.portrait}%;
  }
`

const BgO = styled(motion.div)`
  width: 100%;
  height: 100%;
  z-index: 30;
  pointer-events: none;
`

function BGOverlay() {
  const { bkgfromrv } = useConfigurator()

  const opacity = useMotionValue(bkgfromrv.get() ? 1 : 0)

  function onBg(v) {
    if (!v) animate(opacity, 0, { duration: 0.3 })
  }
  useEffect(() => bkgfromrv.onChange(onBg))

  return (
    <BgO style={{ opacity }}>
      <CfgBackgroundWatch active={true} />
    </BgO>
  )
}

export function CfgContents({ children, Ctx, intro, onClose }) {
  //console.log("reload CfgContents")
  const { startrmc, step, swapping, orientation, launched, expcol, settled } = useFrameAnimation(Ctx)
  const rfmain = useRef()
  const rfupper = useRef()
  const rfswapping = useRef(0)

  function onSwapping(v) {
    clearTimeout(rfswapping.current)
    if (!v) return
    rfswapping.current = setTimeout(() => swapping.set(0), SWAPPING_DELAY)
  }
  useLayoutEffect(() => mvSubscribe(swapping, onSwapping), [])

  function onLaunched(v) {
    //    if (!v) rfupper.current?.querySelector(".cfg-close").classList.add("wv_reveal")
    rfupper.current?.classList[v ? "add" : "remove"]("wv_launched")
  }
  useLayoutEffect(() => mvSubscribe(launched, onLaunched), [])

  function onSettled(v) {
    if (v < 1) return
    setTimeout(() => rfupper.current?.classList.remove("cfg-install"), 600)
  }
  useLayoutEffect(() => mvSubscribe(settled, onSettled), [])

  const keyDownExcludedClassNames = [`.m${hash("price-modal")}`]

  function onKeyDown(e) {
    if (e.code === "Escape") {
      for (const cls of keyDownExcludedClassNames) {
        const el = document.querySelector(cls)
        if (!el) continue
        if (el.contains(e.target)) return false
      }
      if (expcol.get() === 1)
        return expcol.set(0)
      onClose()
    }
  }

  return (
    <Contents
      ref={rfupper}
      className={`cfgv7Contents cfg-install`}
      onKeyDownCapture={onKeyDown}
    >
      <CfgCatalog Ctx={Ctx} />
      <CfgHash Ctx={Ctx} />
      <div ref={rfmain}>
        <CfgBackgrounds Ctx={Ctx} />
        <CfgHeader Ctx={Ctx} />
        <CfgCanvasPanels Ctx={Ctx} />
        <CfgRoller Ctx={Ctx} />
        <CfgSteps Ctx={Ctx} />
        <CfgNavigation Ctx={Ctx} />
        <CfgCanvasTabs Ctx={Ctx} />
        <CfgActive Ctx={Ctx} />
      </div>
      <CfgInstall Ctx={Ctx} />
      <CfgLauncherCard Ctx={Ctx} {...intro} />
      {/*<BGOverlay />*/}
      {children}
    </Contents>
  )
}
